import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { useState } from 'react'

import Link from 'next/link'
import Image from 'next/image'

import api from '@/services/api'
import createOgImage from '@/lib/createOgImage'
import resolveProjectTarget from '@/lib/resolveProjectTarget'

import { Repo } from '@/@types/@repo'

import SEO from '@/components/SEO'
import Masonry from 'react-masonry-component'
import { Card, Container } from '@/styles/pages/Projects'

interface ProjectsStaticProps {
  ogImage: string
  projects: {
    Sistemas: Repo[]
    Layouts: unknown[]
  }
}

type ProjectsProps = InferGetStaticPropsType<
  GetStaticProps<ProjectsStaticProps>
>

export default function Project(props: ProjectsProps) {
  const [projects, setProjects] = useState(props.projects)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const fetchNextPage = () => {
    const cannotLoad = loading || !hasMore

    if (cannotLoad) return

    const newPage = page + 1
    setLoading(true)
    setLoadError(false)

    api
      .get<Repo[]>('/repos', {
        params: {
          page: newPage,
          type: 'FullStack,Web,Mobile,landing-page,Desktop,SDK'
        }
      })
      .then(response => {
        setPage(newPage)
        setProjects(currentProjects => ({
          ...currentProjects,
          Sistemas: [...currentProjects.Sistemas, ...response.data]
        }))
        setHasMore(response.data.length > 0)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }

  const loadMore = e => {
    const elem = e.currentTarget as HTMLDivElement
    const nearBottom =
      elem.scrollTop + elem.clientHeight >= elem.scrollHeight - 240

    if (!nearBottom) return
    fetchNextPage()
  }

  return (
    <Container onScroll={loadMore}>
      <SEO
        title="Carlos Miguel Projetos"
        description={`Meus projetos Pessoais, tanto Apps, como WebSites e Design de layouts.\n
          ${projects.Sistemas.map(project => project.name).join(', ')}
        `}
        image={props.ogImage}
      />
      <h1>Projetos</h1>

      <h3>Sistemas</h3>
      <section id="systems">
        {/* @ts-ignore */}
        <Masonry
          className={'grid'}
          elementType={'ul'}
          options={{
            transitionDuration: 0,
            percentPosition: true
          }}
          disableImagesLoaded={false} // default false
          updateOnEachImageLoad={true}
        >
          {projects.Sistemas.map(project => {
            const template = project.template || {
              url: project.template_url,
              width: 1280,
              height: 720
            }
            const templateUrl =
              template?.url ||
              project.template_url ||
              project.logo_url ||
              project.logo_symbol_url ||
              ''
            const hasTemplate =
              typeof templateUrl === 'string' && templateUrl.length > 0
            const lowerUrl =
              typeof templateUrl === 'string' ? templateUrl.toLowerCase() : ''
            const isGif = lowerUrl.includes('.gif')
            const isSvg = lowerUrl.includes('.svg')
            const isDataUrl = lowerUrl.startsWith('data:')
            const isCachedPreview = lowerUrl.includes('/public/repo-templates/')
            const rawWidth =
              typeof template?.width === 'number' && template.width > 0
                ? template.width
                : 1280
            const rawHeight =
              typeof template?.height === 'number' && template.height > 0
                ? template.height
                : 720
            const target = resolveProjectTarget(project)
            const cardContent = (
              <Card>
                <div
                  className="thumbnail"
                  style={{ aspectRatio: `${rawWidth} / ${rawHeight}` }}
                >
                  {!hasTemplate ? (
                    <img
                      src="/images/bgProject.svg"
                      alt={`${project.name} preview`}
                      width={rawWidth}
                      height={rawHeight}
                      loading="lazy"
                    />
                  ) : isGif || isSvg || isDataUrl || isCachedPreview ? (
                    <img
                      src={templateUrl}
                      alt={project.name}
                      width={rawWidth}
                      height={rawHeight}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <Image
                      src={templateUrl}
                      alt={project.name}
                      quality={60}
                      width={rawWidth > 1000 ? rawWidth * 0.6 : rawWidth}
                      height={
                        rawHeight > 720 || rawWidth > 1000
                          ? rawHeight * 0.6
                          : rawHeight
                      }
                      sizes="(max-width: 992px) calc(100vw - 4rem), min(50vw, 560px)"
                      loading="lazy"
                      style={{ width: '100%', height: '100%' }}
                    />
                  )}
                </div>
                <div className="title-box">
                  <div className="title">{project.name}</div>
                  <div className="description">{project.description}</div>
                </div>
              </Card>
            )

            return (
              <li key={project.id} className="grid-item">
                {target.external ? (
                  <a
                    href={target.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cardContent}
                  </a>
                ) : (
                  <Link prefetch={false} href={target.href}>
                    {cardContent}
                  </Link>
                )}
              </li>
            )
          })}
        </Masonry>
        <div className="loading-state" aria-live="polite">
          {loading && (
            <>
              <span className="spinner" aria-hidden="true" />
              <span>Carregando projetos...</span>
            </>
          )}
          {!loading && loadError && (
            <>
              <span>Não foi possível carregar mais projetos.</span>
              <button type="button" onClick={fetchNextPage}>
                Tentar novamente
              </button>
            </>
          )}
          {!loading && !loadError && !hasMore && (
            <span>Você chegou ao fim da lista.</span>
          )}
        </div>
      </section>
    </Container>
  )
}

export const getStaticProps: GetStaticProps<ProjectsStaticProps> = async () => {
  const ogImage = await createOgImage({
    title: 'Projetos'
  })

  let repos: Repo[] = []
  let layouts: unknown[] = []

  try {
    const reposResponse = await api.get<Repo[]>('/repos', {
      params: {
        page: 0,
        type: 'FullStack,Web,Mobile,landing-page,Desktop,SDK'
      }
    })
    repos = reposResponse.data
  } catch (error) {
    console.error('Failed to fetch repos:', error)
  }

  try {
    const layoutsResponse = await api.get('/layout/list')
    layouts = layoutsResponse.data
  } catch (error) {
    console.error('Failed to fetch layouts:', error)
  }

  return {
    revalidate: 86400,
    props: {
      ogImage,
      projects: {
        Sistemas: repos,
        Layouts: layouts
      }
    }
  }
}
